(() => {
  const selectors = {
    root: '[data-kosbling-collection-stream]',
    grid: '[data-kosbling-collection-grid]',
    card: '[data-product-id]',
    sentinel: '[data-kosbling-collection-sentinel]',
    status: '[data-kosbling-collection-status]',
    retry: '[data-kosbling-collection-retry]'
  };
  const historyKey = 'kosblingProgressiveBrowsing';
  const historyVersion = 1;
  const isRecord = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  const absoluteUrl = (value) => new URL(value, window.location.href);
  const hasSortParameter = (value) => {
    try {
      const source = String(value || '');
      if (!source) return false;
      const parameters = source.includes('?') || source.startsWith('/') || /^[a-z][a-z\d+.-]*:/i.test(source)
        ? absoluteUrl(source).searchParams
        : new URLSearchParams(source.replace(/^\?/, ''));
      return parameters.has('sort_by');
    } catch (error) { return true; }
  };
  const normalizeCurrentSort = () => {
    const current = absoluteUrl(window.location.href);
    if (current.origin !== window.location.origin || !current.searchParams.has('sort_by')) return false;
    current.searchParams.delete('sort_by');
    window.location.replace(current.href);
    return true;
  };
  const normalizingSort = normalizeCurrentSort();
  const compareExactStrings = (left, right) => left === right ? 0 : (left < right ? -1 : 1);
  const queryIdentity = (value) => {
    try {
      const source = String(value || '');
      const query = source.includes('?') ? absoluteUrl(source).search : source;
      const parameters = new URLSearchParams(query.replace(/^\?/, ''));
      parameters.delete('page');
      parameters.delete('section_id');
      parameters.delete('sort_by');
      const entries = [...parameters.entries()];
      entries.sort(([leftKey, leftValue], [rightKey, rightValue]) => {
        const keyOrder = compareExactStrings(leftKey, rightKey);
        return keyOrder || compareExactStrings(leftValue, rightValue);
      });
      return JSON.stringify(entries);
    } catch (error) { return null; }
  };
  const pageNumber = (value) => {
    const source = String(value ?? '');
    if (!/^[1-9]\d*$/.test(source)) return null;
    const page = Number(source);
    return Number.isSafeInteger(page) ? page : null;
  };
  const pageFromUrl = (value) => {
    if (!value || hasSortParameter(value)) return null;
    const pages = absoluteUrl(value).searchParams.getAll('page');
    return pages.length === 1 ? pageNumber(pages[0]) : null;
  };
  const historyNamespace = (state) => {
    if (state !== null && state !== undefined && !isRecord(state)) throw new Error('History state must be an object.');
    const base = state || {};
    const existing = base[historyKey];
    if (existing !== undefined && (!isRecord(existing)
      || existing.version !== historyVersion
      || (existing.rails !== undefined && !isRecord(existing.rails))
      || (existing.collections !== undefined && !isRecord(existing.collections)))) {
      throw new Error('Progressive browsing history is malformed.');
    }
    return { state: base, namespace: existing || { version: historyVersion, rails: {}, collections: {} } };
  };

  class CollectionStream {
    constructor(root, dependencies = {}) {
      this.root = root;
      this.grid = root.querySelector(selectors.grid);
      this.status = root.querySelector(selectors.status);
      this.retry = root.querySelector(selectors.retry);
      this.fetch = dependencies.fetch || window.fetch?.bind(window);
      this.Observer = dependencies.IntersectionObserver || window.IntersectionObserver;
      this.inFlight = false;
      this.disposed = false;
      this.restoring = false;
      this.historyEnabled = true;
      this.initialPage = pageNumber(root.dataset.page);
      this.currentPage = this.initialPage;
      this.initialNextUrl = root.dataset.nextUrl || '';
      this.loadedPages = [];
      this.appendedCards = [];
      this.queryIdentity = Object.hasOwn(root.dataset, 'queryString') && !hasSortParameter(root.dataset.queryString)
        ? queryIdentity(root.dataset.queryString)
        : null;
      this.collectionUrl = this.validCollectionUrl(root.dataset.collectionUrl);
      this.stateId = this.identityKey();
      this.initialProductIds = new Set([...this.grid.querySelectorAll(selectors.card)].map((item) => item.dataset.productId));
      this.productIds = new Set(this.initialProductIds);
      this.onRetry = this.load.bind(this);
      this.onWindowScroll = this.onWindowScroll.bind(this);
      this.onPageHide = this.persistPosition.bind(this);

      let savedEntry = null;
      try {
        if (!this.stateId) throw new Error('Collection stream identity is incomplete.');
        savedEntry = this.readHistoryEntry();
        if (!savedEntry) this.writeHistoryEntry();
      } catch (error) {
        this.historyEnabled = false;
      }

      this.retry?.addEventListener('click', this.onRetry);
      window.addEventListener?.('scroll', this.onWindowScroll, { passive: true });
      window.addEventListener?.('pagehide', this.onPageHide);
      this.sentinel = root.querySelector(selectors.sentinel);
      if (this.Observer && this.sentinel) {
        this.observer = new this.Observer((entries) => {
          this.sentinelIntersecting = entries.some((entry) => entry.isIntersecting);
          if (this.sentinelIntersecting) this.load();
        }, { root: null, rootMargin: '480px 0px' });
        this.observer.observe(this.sentinel);
      }
      if (!this.nextUrl) this.setStatus('All products are loaded.');
      if (savedEntry && this.historyEnabled) {
        this.restoring = true;
        this.restorePromise = this.restore(savedEntry);
      } else this.restorePromise = Promise.resolve(false);
    }

    validCollectionUrl(value) {
      try {
        const url = absoluteUrl(value);
        return url.origin === window.location.origin ? url : null;
      } catch (error) { return null; }
    }

    identityKey() {
      if (!this.root.dataset.sectionId || !this.root.dataset.collectionHandle
        || !this.collectionUrl || !this.queryIdentity || !this.initialPage) return '';
      return JSON.stringify(['collection', this.root.dataset.sectionId, this.root.dataset.collectionHandle,
        this.collectionUrl.pathname, this.queryIdentity]);
    }

    validateHistoryEntry(entry) {
      if (!isRecord(entry) || entry.initialPage !== this.initialPage
        || !Array.isArray(entry.loadedPages) || !Number.isFinite(entry.position) || entry.position < 0) {
        throw new Error('Collection history entry is malformed.');
      }
      let expected = this.initialPage + 1;
      for (const page of entry.loadedPages) {
        if (!Number.isSafeInteger(page) || page !== expected) throw new Error('Collection history pages are not sequential.');
        expected += 1;
      }
      return { loadedPages: [...entry.loadedPages], position: entry.position };
    }

    readHistoryEntry() {
      const { namespace } = historyNamespace(window.history.state);
      const entry = (namespace.collections || {})[this.stateId];
      return entry === undefined ? null : this.validateHistoryEntry(entry);
    }

    writeHistoryEntry() {
      if (!this.historyEnabled) return false;
      const { state, namespace } = historyNamespace(window.history.state);
      const nextNamespace = {
        ...namespace,
        version: historyVersion,
        rails: { ...(namespace.rails || {}) },
        collections: {
          ...(namespace.collections || {}),
          [this.stateId]: {
            initialPage: this.initialPage,
            loadedPages: [...this.loadedPages],
            position: Math.max(0, Number(window.scrollY) || 0),
          },
        },
      };
      window.history.replaceState({ ...state, [historyKey]: nextNamespace }, '', window.location.href);
      return true;
    }

    persistPosition() {
      if (!this.historyEnabled || this.restoring || this.disposed) return;
      try { this.writeHistoryEntry(); } catch (error) { this.rollbackToInitial(true); }
    }

    onWindowScroll() {
      if (this.scrollFrame) return;
      const schedule = window.requestAnimationFrame || ((callback) => callback());
      this.scrollFrame = schedule(() => {
        this.scrollFrame = null;
        this.persistPosition();
      });
    }

    get nextUrl() { return this.root.dataset.nextUrl || ''; }

    setStatus(message) { if (this.status) this.status.textContent = message; }

    isNearEnd() {
      if (!this.sentinel?.getBoundingClientRect) return Boolean(this.sentinelIntersecting);
      const bounds = this.sentinel.getBoundingClientRect();
      return bounds.bottom >= -480 && bounds.top <= (window.innerHeight || 0) + 480;
    }

    requestIdentity(requestUrl, expectedPage = this.currentPage + 1) {
      try {
        if (hasSortParameter(requestUrl)) return null;
        const requested = absoluteUrl(requestUrl);
        const page = pageFromUrl(requestUrl);
        if (requested.origin !== window.location.origin || requested.pathname !== this.collectionUrl?.pathname
          || page !== expectedPage || queryIdentity(requestUrl) !== this.queryIdentity) return null;
        return { page, url: requested };
      } catch (error) { return null; }
    }

    readBatch(sourceRoot, requestUrl) {
      if (hasSortParameter(requestUrl)
        || hasSortParameter(sourceRoot.dataset.queryString)
        || hasSortParameter(sourceRoot.dataset.nextUrl)) return null;
      const requested = absoluteUrl(requestUrl);
      const expectedPage = pageFromUrl(requestUrl);
      if (expectedPage !== this.currentPage + 1) return null;
      const collection = sourceRoot.dataset.collectionHandle;
      if (collection !== this.root.dataset.collectionHandle) return null;
      const sourceCollectionUrl = this.validCollectionUrl(sourceRoot.dataset.collectionUrl);
      if (!sourceCollectionUrl || sourceCollectionUrl.pathname !== this.collectionUrl?.pathname
        || requested.origin !== window.location.origin || requested.pathname !== this.collectionUrl?.pathname) return null;
      if (!Object.hasOwn(sourceRoot.dataset, 'queryString')) return null;
      if (!Object.hasOwn(sourceRoot.dataset, 'page')
        || !Object.hasOwn(sourceRoot.dataset, 'nextUrl')) return null;
      if (queryIdentity(requestUrl) !== this.queryIdentity
        || queryIdentity(sourceRoot.dataset.queryString) !== this.queryIdentity) return null;
      const responsePage = pageNumber(sourceRoot.dataset.page);
      if (responsePage !== expectedPage) return null;
      const nextUrl = sourceRoot.dataset.nextUrl || '';
      if (nextUrl && !this.requestIdentity(nextUrl, responsePage + 1)) return null;
      const sourceGrid = sourceRoot.querySelector(selectors.grid);
      if (!sourceGrid) return null;
      const batchIds = new Set();
      const cards = [...sourceGrid.querySelectorAll(selectors.card)].filter((item) => {
        const id = item.dataset.productId;
        if (!id || this.productIds.has(id)) return false;
        if (batchIds.has(id)) return false;
        batchIds.add(id);
        return true;
      });
      return { cards, page: responsePage, nextUrl };
    }

    appendCards(cards) { this.grid.append(...cards); }

    commitBatch(batch) {
      this.appendCards(batch.cards);
      batch.cards.forEach((item) => {
        const id = item.dataset.productId;
        this.productIds.add(id);
      });
      this.appendedCards.push(...batch.cards);
      this.currentPage = batch.page;
      this.loadedPages.push(batch.page);
      this.root.dataset.page = String(batch.page);
      this.root.dataset.nextUrl = batch.nextUrl;
    }

    rollbackToInitial(disableHistory = false) {
      this.appendedCards.forEach((item) => item.remove?.());
      this.appendedCards = [];
      this.productIds = new Set(this.initialProductIds);
      this.loadedPages = [];
      this.currentPage = this.initialPage;
      this.root.dataset.page = String(this.initialPage || '');
      this.root.dataset.nextUrl = this.initialNextUrl;
      if (disableHistory) this.historyEnabled = false;
      if (this.retry) this.retry.hidden = false;
      this.setStatus('Could not restore more products. Try again.');
      if (this.historyEnabled) {
        try { this.writeHistoryEntry(); } catch (error) { this.historyEnabled = false; }
      }
    }

    async restore(entry) {
      try {
        for (const page of entry.loadedPages) {
          if (pageFromUrl(this.nextUrl) !== page || !await this.load({ replay: true })) {
            throw new Error('Collection replay drifted from its recorded page boundary.');
          }
        }
        window.scrollTo?.({ top: entry.position, left: 0, behavior: 'auto' });
        return true;
      } catch (error) {
        this.rollbackToInitial(false);
        return false;
      } finally { this.restoring = false; }
    }

    async load(options = {}) {
      if (this.inFlight || this.disposed || !this.nextUrl || !this.fetch) return;
      const replay = options?.replay === true;
      if (!this.historyEnabled || (this.restoring && !replay)) return false;
      if (hasSortParameter(this.nextUrl)) {
        if (this.retry) this.retry.hidden = false;
        this.setStatus('Could not load more products. Try again.');
        return false;
      }
      const requestUrl = this.nextUrl;
      if (!this.requestIdentity(requestUrl)) {
        if (this.retry) this.retry.hidden = false;
        this.setStatus('Could not load more products. Try again.');
        return false;
      }
      let continueLoading = false;
      this.inFlight = true;
      this.root.setAttribute('aria-busy', 'true');
      if (this.retry) this.retry.hidden = true;
      this.setStatus('Loading more products.');
      try {
        const response = await this.fetch(requestUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
        const markup = await response.text();
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const sourceRoot = parsed.querySelector(`${selectors.root}[data-section-id="${this.root.dataset.sectionId}"]`);
        const batch = sourceRoot && this.readBatch(sourceRoot, requestUrl);
        if (!batch) throw new Error('Collection response identity did not match the requested next page.');
        if (this.disposed || this.root.isConnected === false) return false;
        this.commitBatch(batch);
        if (!replay) {
          try { this.writeHistoryEntry(); } catch (error) {
            this.rollbackToInitial(true);
            throw error;
          }
          continueLoading = Boolean(batch.cards.length && this.nextUrl && this.isNearEnd());
        }
        if (this.nextUrl) this.setStatus(`${batch.cards.length} more products loaded.`);
        else this.setStatus('All products are loaded.');
        return true;
      } catch (error) {
        if (!this.disposed) {
          if (this.retry) this.retry.hidden = false;
          this.setStatus('Could not load more products. Try again.');
        }
        return false;
      } finally {
        this.inFlight = false;
        this.root.setAttribute('aria-busy', 'false');
        if (continueLoading && !this.disposed) {
          const schedule = window.requestAnimationFrame || ((callback) => callback());
          schedule(() => {
            if (!this.inFlight && !this.disposed && this.nextUrl && this.isNearEnd()) this.load();
          });
        }
      }
    }

    dispose() {
      if (this.disposed) return;
      this.disposed = true;
      this.observer?.disconnect();
      this.retry?.removeEventListener('click', this.onRetry);
      window.removeEventListener?.('scroll', this.onWindowScroll);
      window.removeEventListener?.('pagehide', this.onPageHide);
    }
  }

  const controllers = new WeakMap();
  const mountedRoots = new Set();
  const mount = (root) => {
    if (root && !controllers.has(root)) {
      controllers.set(root, new CollectionStream(root));
      mountedRoots.add(root);
    }
    return controllers.get(root);
  };
  const dispose = (root) => {
    controllers.get(root)?.dispose();
    mountedRoots.delete(root);
  };
  const mountAll = (scope = document) => scope.querySelectorAll?.(selectors.root).forEach(mount);
  const reconcile = () => {
    mountedRoots.forEach((root) => { if (!root.isConnected) dispose(root); });
    mountAll();
  };

  window.KosblingCollectionStream = {
    CollectionStream, mount, dispose, mountAll, reconcile, queryIdentity, hasSortParameter, normalizeCurrentSort
  };
  document.addEventListener('DOMContentLoaded', () => {
    if (normalizingSort) return;
    mountAll();
    const Observer = window.MutationObserver || MutationObserver;
    if (Observer) {
      const mutations = new Observer(reconcile);
      mutations.observe(document.documentElement, { childList: true, subtree: true });
    }
  });
  document.addEventListener('shopify:section:load', (event) => {
    if (!normalizingSort) mountAll(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll?.(selectors.root).forEach(dispose));
})();
